import { useState, useMemo } from 'react';
import { Search, Plus, CreditCard, User, Lock, CheckCircle, XCircle, MoreVertical, Filter } from 'lucide-react';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import CardEditor from '../cards/CardEditor';

const STATUS_STYLES = {
  active: { bg: colors.greenDim, color: colors.green, label: 'Active', icon: CheckCircle },
  locked: { bg: colors.orangeDim, color: colors.orange, label: 'Locked', icon: Lock },
  closed: { bg: colors.redDim, color: colors.red, label: 'Closed', icon: XCircle },
};

export default function CardsTab({ permissions }) {
  const { getAllCards, getCardWithUser, loading, users } = useData();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Get all cards with user assignment info
  const cardsWithUsers = useMemo(() => {
    const allCards = getAllCards();
    return allCards.map(card => {
      const assignedUser = users.find(u =>
        (u.assignedCards || []).includes(card.cardLast4)
      );
      return {
        ...card,
        assignedUser: assignedUser ? {
          uid: assignedUser.uid,
          displayName: assignedUser.displayName,
          email: assignedUser.email,
        } : null,
      };
    });
  }, [getAllCards, users]);

  // Apply filters
  const filteredCards = useMemo(() => {
    let result = cardsWithUsers;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.cardLast4.includes(term) ||
        (c.holderName || '').toLowerCase().includes(term) ||
        (c.cardName || '').toLowerCase().includes(term) ||
        (c.assignedUser?.displayName || '').toLowerCase().includes(term) ||
        (c.assignedUser?.email || '').toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }

    // Apply assignment filter
    if (assignmentFilter === 'assigned') {
      result = result.filter(c => c.assignedUser);
    } else if (assignmentFilter === 'unassigned') {
      result = result.filter(c => !c.assignedUser);
    }

    return result;
  }, [cardsWithUsers, searchTerm, statusFilter, assignmentFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const allCards = cardsWithUsers;
    return {
      total: allCards.length,
      active: allCards.filter(c => c.status === 'active').length,
      locked: allCards.filter(c => c.status === 'locked').length,
      closed: allCards.filter(c => c.status === 'closed').length,
      assigned: allCards.filter(c => c.assignedUser).length,
    };
  }, [cardsWithUsers]);

  const handleEditCard = (card) => {
    setSelectedCard(card);
    setIsCreating(false);
    setShowEditor(true);
  };

  const handleCreateCard = () => {
    setSelectedCard(null);
    setIsCreating(true);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedCard(null);
    setIsCreating(false);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            Card Management
          </h1>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            Manage company cards and assignments
          </p>
        </div>
        <Button onClick={handleCreateCard}>
          <Plus size={18} />
          Add Card
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{stats.total}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Total Cards</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.green }}>{stats.active}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Active</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.orange }}>{stats.locked}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Locked</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.red }}>{stats.closed}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Closed</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.yellow }}>{stats.assigned}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Assigned</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: colors.textMuted }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by card number, holder, or assigned user..."
              className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
              style={{
                background: colors.inputBg,
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={{
              background: colors.inputBg,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="locked">Locked</option>
            <option value="closed">Closed</option>
          </select>

          {/* Assignment Filter */}
          <select
            value={assignmentFilter}
            onChange={(e) => setAssignmentFilter(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={{
              background: colors.inputBg,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <option value="all">All Cards</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
      </Card>

      {/* Cards Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Card
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Holder / Card Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Assigned To
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Agreement
                </th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCards.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8" style={{ color: colors.textMuted }}>
                    No cards found
                  </td>
                </tr>
              ) : (
                filteredCards.map(card => {
                  const statusStyle = STATUS_STYLES[card.status] || STATUS_STYLES.active;
                  const StatusIcon = statusStyle.icon;

                  return (
                    <tr
                      key={card.cardLast4}
                      className="cursor-pointer hover:opacity-80"
                      style={{ borderBottom: `1px solid ${colors.border}` }}
                      onClick={() => handleEditCard(card)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="p-2 rounded-lg"
                            style={{ background: colors.inputBg }}
                          >
                            <CreditCard size={18} style={{ color: colors.yellow }} />
                          </div>
                          <span className="font-mono font-medium" style={{ color: colors.textPrimary }}>
                            ****{card.cardLast4}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p style={{ color: colors.textPrimary }}>
                            {card.holderName || '-'}
                          </p>
                          {card.cardName && card.cardName !== card.holderName && (
                            <p className="text-sm" style={{ color: colors.textMuted }}>
                              On card: {card.cardName}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1"
                          style={{ background: statusStyle.bg, color: statusStyle.color }}
                        >
                          <StatusIcon size={12} />
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {card.assignedUser ? (
                          <div className="flex items-center gap-2">
                            <User size={14} style={{ color: colors.green }} />
                            <span style={{ color: colors.textSecondary }}>
                              {card.assignedUser.displayName || card.assignedUser.email}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: colors.textMuted }}>Unassigned</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {card.hasAgreement ? (
                          <CheckCircle size={18} style={{ color: colors.green }} />
                        ) : (
                          <span style={{ color: colors.textMuted }}>-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCard(card);
                          }}
                          className="p-2 rounded-lg hover:opacity-80"
                          style={{ color: colors.textMuted }}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Card Editor Modal */}
      {showEditor && (
        <CardEditor
          card={isCreating ? null : selectedCard}
          isNew={isCreating}
          onClose={handleCloseEditor}
        />
      )}
    </div>
  );
}
